import * as React from "react";

import {
  AppShell,
  Box,
  Burger,
  Group,
  Header,
  MediaQuery,
  Navbar,
  NavLink,
  Stack,
  Text,
  Title,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconChecklist,
  IconHome2,
  IconInfoCircle,
  IconPhoto,
  IconSparkles,
  IconStar,
  type Icon as TablerIcon,
  IconCircle,
} from "@tabler/icons-react";
import { Helmet } from "react-helmet";
import { Link, Outlet, ScrollRestoration, useLocation } from "react-router-dom";

import { latestUpdateDate, latestUpdateId } from "../../content/updates/updates";
import { useAppState } from "../../hooks/useAppState";
import { useGoatCounter } from "../../hooks/useGoatCounter";
import { NavbarLibraryList } from "../navigation/NavbarLibraryList";

export const PageStructure = (): JSX.Element => {
  const [navigationOpened, setNavigationOpened] = React.useState(false);
  const theme = useMantineTheme();
  const isSmallerThanSm = useMediaQuery(`(max-width: ${theme.breakpoints.sm}px`);
  const closeNavigationIfSm = React.useCallback(() => {
    if (isSmallerThanSm) {
      setNavigationOpened(false);
    }
  }, [isSmallerThanSm, setNavigationOpened]);

  return (
    <AppShell
      padding={0}
      fixed={false}
      navbar={<AppNavbar hidden={!navigationOpened} onSelect={closeNavigationIfSm} />}
      header={
        <AppHeader navigationOpened={navigationOpened} setNavigationOpened={setNavigationOpened} />
      }
      navbarOffsetBreakpoint="sm"
      styles={(theme) => ({
        root: {
          minHeight: "100vh",
        },
        main: {
          backgroundColor:
            theme.colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.gray[0],
          padding: "0 0",
          width: "calc(100vw - 250px)",
          minHeight: "calc(100vh - 60px)",
        },
      })}
    >
      <Helmet
        titleTemplate="%s | fs-checklist-studio"
        defaultTitle="fs-checklist-studio - Create realistic custom flight simulator checklists"
      >
        <meta
          name="description"
          content="fs-checklist-studio is a fast and easy way to create realistic flight simulator checklists directly in your browser.
      Compose checklists from your own content or by combining dozens of presets and export to PDF. No account, login, or email required."
        />
      </Helmet>
      <Outlet />
      <ScrollRestoration />
    </AppShell>
  );
};

const AppHeader = ({
  navigationOpened,
  setNavigationOpened,
}: {
  navigationOpened: boolean;
  setNavigationOpened: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const theme = useMantineTheme();
  const isWiderThanSmWidth = useMediaQuery(`(min-width: ${theme.breakpoints.sm}px)`);
  const titleOrder = useMediaQuery(`(min-width: 373px`) ? 1 : 2;
  return (
    <Header
      height={60}
      sx={(theme) => ({
        backgroundImage: theme.fn.linearGradient(45, "cyan", "blue"),
        color: theme.white,
      })}
      fixed={!isWiderThanSmWidth && navigationOpened}
    >
      <Group sx={{ height: "100%" }} px={theme.spacing.xs} spacing={2} position="apart" noWrap>
        <Title
          order={titleOrder}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
          }}
          px={5}
        >
          <Group spacing="xs">
            <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
              <IconChecklist />
            </MediaQuery>
            fs-checklist-studio
          </Group>
        </Title>
        <MediaQuery largerThan="md" styles={{ display: "none" }}>
          <Box
            sx={(theme) => ({
              backgroundColor: "black",
              borderRadius: theme.radius.md,
              lineHeight: 1,
              padding: "2px",
            })}
          >
            <Burger
              size="sm"
              opened={navigationOpened}
              onClick={() => setNavigationOpened((o) => !o)}
              color="white"
              transitionDuration={100}
            />
          </Box>
        </MediaQuery>
      </Group>
    </Header>
  );
};

const AppNavbar = ({ hidden, onSelect }: { hidden: boolean; onSelect: () => void }) => {
  const location = useLocation();
  const editorCurrent = location.pathname === "/editor";
  const eventCounter = useGoatCounter();

  const buildStamp: string = import.meta.env.VITE_BUILD_STAMP
    ? new Date(import.meta.env.VITE_BUILD_STAMP).toLocaleString()
    : import.meta.env.DEV
    ? "(dev)"
    : "unknown";

  const theme = useMantineTheme();

  const hasNewUpdates = useAppState((state) => state.lastReadUpdate.id !== latestUpdateId);

  return (
    <MediaQuery smallerThan="sm" styles={{ position: "fixed", width: "100%" }}>
      <Navbar
        width={{ base: 250 }}
        height="inherit"
        pt="xs"
        pb="xs"
        style={{ minWidth: "250px" }}
        hiddenBreakpoint="md"
        hidden={hidden}
      >
        <Navbar.Section grow>
          <Stack justify="center" align="center" spacing="sm">
            <NavbarLink href="/" icon={IconHome2} label="Home" onSelect={onSelect} />
            <Stack spacing={0} sx={() => ({ width: "100%" })}>
              <NavbarLink
                href="/editor"
                icon={IconChecklist}
                label="Checklist Editor"
                onSelect={onSelect}
              />
              <NavbarLibraryList editorActive={editorCurrent} onSelect={onSelect} />
            </Stack>
            <NavbarLink href="/manage" icon={IconStar} label="My Checklists" onSelect={onSelect} />
            <NavbarLink
              href="/templates"
              icon={IconPhoto}
              label="Template Gallery"
              onSelect={onSelect}
            />
            <NavbarLink href="/about" icon={IconInfoCircle} label="About" onSelect={onSelect} />
            <NavbarLink
              href="/updates"
              icon={IconSparkles}
              iconColor={hasNewUpdates ? theme.colors.indigo[7] : undefined}
              label={
                <Group spacing={"xs"}>
                  <Text
                    variant={hasNewUpdates ? "gradient" : undefined}
                    gradient={{ from: "blue.7", to: "violet.8", deg: 45 }}
                  >
                    What's New
                  </Text>
                  {hasNewUpdates && (
                    <IconCircle
                      style={{ fill: theme.colors.indigo[4] }}
                      size={12}
                      color={theme.colors.indigo[4]}
                    />
                  )}
                </Group>
              }
              onSelect={onSelect}
              description={
                <Text
                  fw={hasNewUpdates ? 800 : undefined}
                  variant={hasNewUpdates ? "gradient" : undefined}
                  gradient={{ from: "blue.7", to: "violet.8", deg: 45 }}
                >
                  {latestUpdateDate}
                </Text>
              }
            />
          </Stack>
        </Navbar.Section>
        <Navbar.Section pt="xl">
          <Group position="apart" mr="xs">
            <Text ml="sm" fz="xs" c="dimmed">
              Created by{" "}
              <a
                href="https://fredhatfull.com"
                target="_blank"
                onClick={() => {
                  eventCounter("fhats-web-link");
                }}
                data-goatcounter-click="fhatsweb-link"
                style={{ color: "inherit" }}
              >
                Fred Hatfull
              </a>
            </Text>
            <Tooltip label={buildStamp}>
              <Text ml="sm" fz="xs" c="dimmed">
                {__GIT_COMMIT_SHORT_HASH__}
              </Text>
            </Tooltip>
          </Group>
        </Navbar.Section>
      </Navbar>
    </MediaQuery>
  );
};

interface NavbarLinkProps {
  icon: TablerIcon;
  iconColor?: string;
  label: React.ReactNode;
  href: string;
  description?: React.ReactNode;
}

function NavbarLink({
  icon,
  iconColor,
  label,
  href,
  onSelect,
  description,
}: NavbarLinkProps & { onSelect: () => void }) {
  const location = useLocation();
  const current = location.pathname === href;
  return (
    <FullNavLink
      icon={icon}
      iconColor={iconColor}
      label={label}
      href={href}
      active={current}
      onSelect={onSelect}
      description={description}
    />
  );
}

const FullNavLink = ({
  icon: Icon,
  iconColor,
  label,
  href,
  active,
  onSelect,
  description,
}: {
  icon: TablerIcon;
  iconColor?: string;
  label: React.ReactNode;
  href: string;
  active: boolean;
  onSelect: () => void;
  description?: React.ReactNode;
}): JSX.Element => {
  return (
    <NavLink
      component={Link}
      to={href}
      label={label}
      icon={<Icon size={20} stroke={2} color={iconColor} />}
      color="dark"
      variant="filled"
      active={active}
      onClick={onSelect}
      styles={(theme) => ({
        label: {
          fontWeight: 600,
          fontSize: theme.fontSizes.md,
        },
      })}
      description={description}
    />
  );
};
